import React from 'react';
import { DataGrid } from '@mui/x-data-grid';
import { Box, Chip, IconButton, Tooltip, Typography } from '@mui/material';
import StatusChip from '../../../components/StatusChip';
import EditIcon from '@mui/icons-material/Edit';
import { currencyFormatter } from '../../../utils/currencyFormat';
import CustomPagination from '../../../components/customPagination';
import { formatDate } from '../../../utils/formatDate';
import { useGetPurchaseInvoiceRegistersHandler } from '../dataHandlers/getPurchaseInvoiceRegistersHandler';
import { useNavigate } from 'react-router-dom';
import { useTranslatedMessages } from '../../../hooks/useTranslatedMessages';
import { usePermissions } from '../../../hooks/usePermissions';
import { UPDATE_PURCHASE_REGISTER } from '../../../constants/eInvoicePermissions';
import PermissionDeniedWrapper from '../../../components/PermissionDeniedWrapper';
import { useAccessDeniedMessage } from '../../../hooks/useAccessDeniedMessage';
import { RCM_TAX_CATEGORY_CODE } from '../../../constants/defaults';

const getDocumentId = (row) => row.uuid || row.id;

const PurchaseInvoiceRegisterTable = ({ handlePaginationChange, searchQuery = {} }) => {
  const navigate = useNavigate();
  const { purchaseInvoiceRegister } = useTranslatedMessages();
  const t = purchaseInvoiceRegister?.table ?? {};
  const { hasEInvoicePermission } = usePermissions();
  const accessDeniedMessage = useAccessDeniedMessage();
  const canUpdate = hasEInvoicePermission(UPDATE_PURCHASE_REGISTER);
  const { registers, totalDocuments, isLoading } = useGetPurchaseInvoiceRegistersHandler({ searchQuery });

  const navigateToRow = (row) => {
    const documentId = getDocumentId(row);
    if (row.document_mode === 'E_INVOICE') {
      navigate(`/purchases/documents/invoices/${documentId}`);
    } else {
      navigate(`/purchase-invoice-register/${documentId}`);
    }
  };

  const columns = [
    {
      headerName: t.source ?? 'Source',
      field: 'document_mode',
      flex: 0.8,
      renderCell: (params) => {
        const isEInvoice = params.row.document_mode === 'E_INVOICE';
        return (
          <StatusChip
            label={
              isEInvoice
                ? (t.source_e_invoice ?? 'E-Invoice')
                : (t.source_non_e_invoice ?? t.source_register ?? 'Non-E-Invoice')
            }
            size="small"
            color={isEInvoice ? 'success' : 'default'}
            icon={'none'}
          />
        );
      },
    },
    {
      headerName: t.rcm ?? 'RCM',
      field: 'is_rcm_badge',
      flex: 0.7,
      sortable: false,
      renderCell: (params) => {
        const documentLines = params.row?.document_lines;
        const isRcm = documentLines?.some((line) => line?.classified_tax_category?.id === RCM_TAX_CATEGORY_CODE);
        if (!isRcm) {
          return '—';
        }
        return <Chip label={'RCM'} size="small" color="primary" variant="outlined" />;
      },
    },
    {
      headerName: t.invoice_no ?? 'Invoice No',
      field: 'document_number',
      flex: 1,
      renderCell: (params) => (
        <Typography
          component="span"
          variant="span"
          onClick={() => navigateToRow(params.row)}
          sx={{
            cursor: 'pointer',
            '&:hover': { textDecoration: 'underline', color: 'primary.main' },
          }}
        >
          {params.value}
        </Typography>
      ),
    },
    {
      headerName: t.supplier_info ?? 'Supplier',
      field: 'accounting_supplier_party',
      flex: 1,
      valueGetter: (params) => params?.name ?? t.not_available ?? 'N/A',
    },
    {
      headerName: t.invoice_date ?? 'Invoice Date',
      field: 'issue_date',
      flex: 1,
      valueGetter: (params) => formatDate(params),
    },
    {
      headerName: t.amount ?? 'Amount',
      field: 'payable_amount',
      flex: 1,
      renderCell: ({ row }) =>
        row.document_currency_code === 'AED'
          ? `AED ${currencyFormatter().format(row.payable_amount)}`
          : currencyFormatter(row.document_currency_code).format(row.payable_amount),
    },
    {
      headerName: t.vat_amount ?? 'VAT Amount',
      field: 'tax_amount',
      flex: 1,
      renderCell: ({ row }) =>
        row.document_currency_code === 'AED'
          ? `AED ${currencyFormatter().format(row.tax_amount ?? 0)}`
          : currencyFormatter(row.document_currency_code).format(row.tax_amount ?? 0),
    },
    {
      field: 'actions',
      headerName: t.actions ?? 'Actions',
      flex: 0.6,
      sortable: false,
      renderCell: (params) => {
        if (params.row.document_mode !== 'NON_E_INVOICE') {
          return null;
        }
        return (
          <PermissionDeniedWrapper show={!canUpdate} title={accessDeniedMessage}>
            <Tooltip title={t.edit ?? 'Edit'}>
              <IconButton
                size="small"
                disabled={!canUpdate}
                onClick={() => navigate(`/purchase-invoice-register/${getDocumentId(params.row)}/edit`)}
                data-testid="edit-register-button"
              >
                <EditIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </PermissionDeniedWrapper>
        );
      },
    },
  ];

  return (
    <Box>
      <DataGrid
        rows={registers}
        columns={columns}
        getRowId={(row) => row.id || row.uuid}
        paginationMode="server"
        rowCount={totalDocuments}
        loading={isLoading}
        initialState={{
          pagination: {
            paginationModel: {
              pageSize: 10,
            },
          },
        }}
        pageSizeOptions={[10, 25]}
        disableRowSelectionOnClick
        onPaginationModelChange={(model) => {
          handlePaginationChange(model.page, model.pageSize);
        }}
        slots={{
          pagination: CustomPagination,
        }}
      />
    </Box>
  );
};

export default PurchaseInvoiceRegisterTable;
